import React, { useEffect, useMemo, useState } from 'react';
import { APITypes } from '../../../../primary-record-types';
import ProfileSelect from './ProfileSelect';
import DomainSelect, { shouldRenderDomainSelect } from './DomainSelect';
import { useNoteDestinationResourceCategory } from '../../../../hooks/api/useHasNotePermission';
import CareTeamMemberSelect, { shouldRenderCareTeamMemberSelect } from './CareTeamMemberSelect';
import ResourceSelect, { shouldRenderResourceSelect } from './ResourceSelect';
import { getIntl } from '../../../../utils/intl';
import {
    NoteDestination,
    isOrganizationClientRecordNoteDestination,
    isOrganizationNoteDestination,
    isUserNoteDestination,
    noteDetailsToDestination,
} from '../utils/types';
import { AnimateLoadedContent } from '../../../v2BaseComponents/Loader';
import Stack, { contentStackSpacing } from '../../../v2BaseComponents/Stack';
import { ActionButtonLayout } from '../../../v2BaseComponents/ActionButtonLayout';
import DetailsLayout from '../../../v2BaseComponents/DetailsLayout';
import MainContentContainer from '../../../v2BaseComponents/MainContentContainer';

const intl = getIntl('transfer-note', {
    transfer: 'Move Note',
});

export type TransferNoteOwnProps = {
    note: APITypes.notesService.NoteDetails;
    onTransfer: (destination: NoteDestination) => void;
    isTransferring: boolean;
};

export type TransferNoteProps = TransferNoteOwnProps;

const TransferNote: React.FC<TransferNoteProps> = ({ note, onTransfer, isTransferring }) => {
    const [noteDestination, setNoteDestination] = useState<NoteDestination>(noteDetailsToDestination(note));
    // anytime parent updates note, reflect that
    useEffect(() => {
        setNoteDestination(noteDetailsToDestination(note));
    }, [note]);
    const noteResourceCategory = useNoteDestinationResourceCategory(noteDestination);

    const getCanSubmit = () => {
        if (
            isUserNoteDestination(noteDestination) ||
            isOrganizationNoteDestination(noteDestination) ||
            isOrganizationClientRecordNoteDestination(noteDestination)
        )
            return false;

        return !!noteDestination.medicalProfileId;
    };

    const isProfileNotAdjustable = useMemo(() => !!note.medicalProfileId, [note.medicalProfileId]);

    return (
        <AnimateLoadedContent isLoading={noteResourceCategory.isLoading}>
            {() => (
                <ActionButtonLayout
                    actions={[
                        {
                            title: intl('transfer'),
                            onPress: () => {
                                onTransfer(noteDestination);
                            },
                            isDisabled: !getCanSubmit() || isTransferring,
                            testID: 'note-transfer-submit-button',
                            icon: 'CheckCircle',
                        },
                    ]}
                >
                    <DetailsLayout>
                        <MainContentContainer>
                            <Stack fullWidth vertical space={contentStackSpacing} align="center">
                                {isProfileNotAdjustable ? null : (
                                    <ProfileSelect
                                        value={noteDestination}
                                        onChange={setNoteDestination}
                                        isDisabled={isProfileNotAdjustable || isTransferring}
                                        changeDisabled={isProfileNotAdjustable}
                                    />
                                )}
                                {shouldRenderDomainSelect(noteDestination) ? (
                                    <DomainSelect
                                        value={noteDestination}
                                        onChange={setNoteDestination}
                                        isDisabled={isTransferring}
                                    />
                                ) : null}
                                {shouldRenderCareTeamMemberSelect(noteDestination) ? (
                                    <CareTeamMemberSelect
                                        value={noteDestination}
                                        onChange={setNoteDestination}
                                        isDisabled={isTransferring}
                                    />
                                ) : null}
                                {shouldRenderResourceSelect(noteDestination) ? (
                                    <ResourceSelect
                                        value={noteDestination}
                                        onChange={setNoteDestination}
                                        isDisabled={isTransferring}
                                    />
                                ) : null}
                            </Stack>
                        </MainContentContainer>
                    </DetailsLayout>
                </ActionButtonLayout>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(TransferNote);
